import { useEffect, useState, useCallback } from "react";
import { Megaphone, Plus, Trash2, Check, Eye, AlertTriangle, Users, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HR_WRITE, HR_VIEW_ALL, fmtDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CATEGORIES = {
  ATTENDANCE: "Pengumuman Absensi",
  HOLIDAY: "Info Libur",
  EVENT: "Acara / Event",
  REMINDER: "Pengingat",
  SYSTEM: "Notifikasi Sistem",
};

const MEMO_TYPES = {
  EDARAN: "Memo Edaran (semua divisi)",
  INTERNAL: "Memo Internal (divisi tertentu)",
};

const PRIORITY = {
  NORMAL: { label: "Normal", cls: "bg-slate-100 text-slate-700" },
  PENTING: { label: "Penting", cls: "bg-amber-100 text-amber-800" },
  URGENT: { label: "URGENT", cls: "bg-rose-100 text-rose-800" },
  TINGGI: { label: "Penting", cls: "bg-amber-100 text-amber-800" },
};

const EMPTY = { title: "", body: "", category: "ATTENDANCE", priority: "NORMAL", require_ack: false, memo_type: "EDARAN", target_departments: [] };

export default function Announcements() {
  const { user } = useAuth();
  const canWrite = HR_WRITE.includes(user.role);
  const canTrack = HR_VIEW_ALL.includes(user.role);
  const [list, setList] = useState([]);
  const [depts, setDepts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [tracking, setTracking] = useState(null);

  const load = useCallback(() => {
    api.get("/announcements").then((r) => setList(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.post("/announcements/read-all").catch(() => {});
    if (HR_WRITE.includes(user.role)) api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
  }, [load, user.role]);

  const deptName = (id) => depts.find((d) => d.id === id)?.name || id;
  const toggleDept = (id) => setF((prev) => ({
    ...prev,
    target_departments: prev.target_departments.includes(id)
      ? prev.target_departments.filter((x) => x !== id)
      : [...prev.target_departments, id],
  }));

  const submit = async () => {
    if (!f.title || !f.body) return toast.error("Judul dan isi wajib diisi");
    if (f.memo_type === "INTERNAL" && f.target_departments.length === 0) return toast.error("Pilih minimal satu divisi tujuan untuk Memo Internal");
    try {
      await api.post("/announcements", f);
      toast.success("Memo dipublikasikan");
      setOpen(false);
      setF(EMPTY);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => { try { await api.delete(`/announcements/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const ack = async (id) => {
    try { await api.post(`/announcements/${id}/acknowledge`); toast.success("Konfirmasi tersimpan"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openTracking = async (a) => {
    try { const { data } = await api.get(`/announcements/${a.id}/reads`); setTracking({ ...data, title: a.title }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Pusat Komunikasi</h1>
          <p className="mt-1 text-sm text-slate-500">Memo Internal & Edaran PT Showty Global International dengan pelacakan baca.</p>
        </div>
        {canWrite && (
          <Button data-testid="new-announcement-button" onClick={() => setOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" /> Buat Memo
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {list.length === 0 && (
          <Card className="flex flex-col items-center gap-2 rounded-2xl border-dashed p-10 text-center text-slate-400">
            <Megaphone className="h-8 w-8" /><p>Belum ada memo.</p>
          </Card>
        )}
        {list.map((a) => {
          const pr = PRIORITY[a.priority] || PRIORITY.NORMAL;
          const urgent = a.priority === "URGENT";
          const internal = a.memo_type === "INTERNAL";
          return (
            <Card key={a.id} data-testid="announcement-card"
              className={`rounded-2xl p-5 ${urgent ? "border-2 border-rose-300 bg-rose-50/50 dark:border-rose-800 dark:bg-rose-950/20" : "border-slate-200 dark:border-slate-800"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {urgent && <AlertTriangle className="h-4 w-4 text-rose-600" />}
                    <p className="font-heading text-lg font-semibold">{a.title}</p>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">{CATEGORIES[a.category] || "Memo"}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${internal ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>
                      {internal ? <Building2 className="h-3 w-3" /> : <Users className="h-3 w-3" />}
                      {internal ? "Internal" : "Edaran"}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${pr.cls}`}>{pr.label}</span>
                    {!a.read && <span className="rounded-full bg-sky-500 px-2 py-0.5 text-[10px] font-medium text-white">Baru</span>}
                  </div>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{a.body}</p>
                  {internal && canTrack && a.target_departments?.length > 0 && (
                    <p className="mt-1 text-xs text-violet-600">Divisi: {a.target_departments.map(deptName).join(", ")}</p>
                  )}
                  <p className="mt-2 text-xs text-slate-400">
                    {a.created_by_name ? `${a.created_by_name} · ` : ""}{fmtDate(a.created_at)}
                    {canTrack && a.read_count != null && ` · ${a.read_count} dibaca`}
                  </p>

                  {a.require_ack && (
                    <div className="mt-3">
                      {a.acknowledged ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                          <Check className="h-3.5 w-3.5" /> Sudah dikonfirmasi
                        </span>
                      ) : (
                        <Button size="sm" data-testid="acknowledge-button" onClick={() => ack(a.id)} className="gap-1 bg-emerald-600 hover:bg-emerald-700">
                          <Check className="h-4 w-4" /> Saya sudah membaca & memahami
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  {canTrack && (
                    <Button size="icon" variant="ghost" title="Lacak baca" data-testid="track-reads-button" onClick={() => openTracking(a)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  {canWrite && (
                    <Button size="icon" variant="ghost" data-testid="delete-announcement-button" onClick={() => del(a.id)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Buat Memo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Judul</Label>
              <Input data-testid="announcement-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Jenis Memo</Label>
                <Select value={f.memo_type} onValueChange={(v) => setF({ ...f, memo_type: v })}>
                  <SelectTrigger data-testid="announcement-memotype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(MEMO_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Kategori</Label>
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger data-testid="announcement-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>

            {f.memo_type === "INTERNAL" && (
              <div className="space-y-2 rounded-xl border border-dashed border-violet-300 p-3 dark:border-violet-800" data-testid="target-dept-box">
                <Label className="text-xs font-semibold">Divisi Tujuan <span className="text-rose-500">*</span></Label>
                <div className="grid max-h-40 grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2">
                  {depts.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 text-sm">
                      <Checkbox data-testid={`target-dept-${d.id}`} checked={f.target_departments.includes(d.id)} onCheckedChange={() => toggleDept(d.id)} />
                      {d.name}
                    </label>
                  ))}
                  {depts.length === 0 && <p className="text-xs text-slate-400">Tidak ada divisi.</p>}
                </div>
              </div>
            )}

            <div className="space-y-2"><Label>Prioritas</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="announcement-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="PENTING">Penting</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Isi</Label>
              <Textarea data-testid="announcement-body-input" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox data-testid="require-ack-checkbox" checked={f.require_ack} onCheckedChange={(v) => setF({ ...f, require_ack: !!v })} />
              Wajib konfirmasi baca dari karyawan
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="publish-announcement-button" onClick={submit}>Publikasikan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Read tracking dialog */}
      <Dialog open={!!tracking} onOpenChange={(o) => !o && setTracking(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pelacakan Baca</DialogTitle></DialogHeader>
          {tracking && (
            <div className="space-y-4">
              <p className="text-sm font-medium">{tracking.title}</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl bg-slate-100 p-3 dark:bg-slate-800"><p className="font-mono text-2xl font-bold">{tracking.delivered}</p><p className="text-xs text-slate-500">Terkirim</p></div>
                <div className="rounded-xl bg-emerald-100 p-3 dark:bg-emerald-950/40"><p className="font-mono text-2xl font-bold text-emerald-700">{tracking.read}</p><p className="text-xs text-slate-500">Dibaca</p></div>
                <div className="rounded-xl bg-rose-100 p-3 dark:bg-rose-950/40"><p className="font-mono text-2xl font-bold text-rose-700">{tracking.unread}</p><p className="text-xs text-slate-500">Belum</p></div>
              </div>
              <p className="text-center text-sm font-semibold text-primary">Read Rate: {tracking.read_rate}%</p>
              {tracking.unread_names?.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium text-slate-500">Belum membaca:</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{tracking.unread_names.join(", ")}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
